import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, Col, Container, Row, FormGroup, Label, Input, Button, UncontrolledTooltip } from 'reactstrap';
import { useTareasEnProceso } from '../../hooks/useTareasEnProceso';
import DataTableContainer from '../../components/Common/DataTableContainer';
import PDFViewerModal from '../../components/Common/PDFViewerModal';
import AsignarUsuarioModal from '../../components/Modals/AsignarUsuarioModal';
import EstadisticasOrdenButton from '../../components/Estadisticas/EstadisticasOrdenButton';
import EstadisticasOrdenModal from '../../components/Estadisticas/EstadisticasOrdenModal';
import { createModuleLogger } from '../../utils/logger';

// Crear logger para este módulo
const tareasLogger = createModuleLogger('TareasEnProceso');

const TareasEnProceso = () => {
  const {
    tareas,
    loading,
    error,
    fetchTareasEnProceso,
    clearError
  } = useTareasEnProceso();

  // Estado para filtros
  const [filtros, setFiltros] = useState({
    estado: 'todos',
    plan: 'todos',
    orden: ''
  });

  // Estado para paginación
  const [paginacion, setPaginacion] = useState({
    pagina: 1,
    porPagina: 25
  });

  // Estados para modal PDF
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfModalData, setPdfModalData] = useState({
    ordenId: null,
    filename: null
  });

  // Estados para modal de asignación de usuarios
  const [isAsignarUsuarioModalOpen, setIsAsignarUsuarioModalOpen] = useState(false);
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);

  // Estados para modal de estadísticas
  const [modalEstadisticas, setModalEstadisticas] = useState(false);
  const [ordenParaEstadisticas, setOrdenParaEstadisticas] = useState(null);

  // Filtrar tareas basado en los filtros aplicados
  const tareasFiltradas = useMemo(() => {
    let resultado = tareas;

    // Filtro por estado
    if (filtros.estado !== 'todos') {
      resultado = resultado.filter(tarea => {
        const progreso = tarea.progreso;
        if (!progreso) return false;

        switch (filtros.estado) {
          case 'pendiente':
            return progreso.completados === 0;
          case 'en_proceso':
            return progreso.completados > 0 && progreso.pendientes > 0;
          case 'completada':
            return progreso.pendientes === 0;
          default:
            return true;
        }
      });
    }

    // Filtro por plan
    if (filtros.plan !== 'todos') {
      resultado = resultado.filter(tarea => 
        tarea.planInfo?.nombre === filtros.plan
      );
    }

    // Filtro por número de orden
    if (filtros.orden) {
      resultado = resultado.filter(tarea => 
        tarea.ordenInfo?.numero?.toString().includes(filtros.orden)
      );
    }

    return resultado;
  }, [tareas, filtros]);

  // Aplicar paginación a las tareas filtradas
  const tareasPaginadas = useMemo(() => {
    const inicio = (paginacion.pagina - 1) * paginacion.porPagina;
    const fin = inicio + paginacion.porPagina;
    return tareasFiltradas.slice(inicio, fin);
  }, [tareasFiltradas, paginacion]);

  // Calcular información de paginación
  const infoPaginacion = useMemo(() => {
    const total = tareasFiltradas.length;
    const totalPaginas = Math.ceil(total / paginacion.porPagina);
    const inicio = (paginacion.pagina - 1) * paginacion.porPagina + 1;
    const fin = Math.min(paginacion.pagina * paginacion.porPagina, total);
    
    return {
      total,
      totalPaginas,
      inicio,
      fin,
      paginaActual: paginacion.pagina
    };
  }, [tareasFiltradas, paginacion]);

  // Obtener planes únicos para el filtro
  const planesUnicos = useMemo(() => {
    const planes = tareas.map(tarea => tarea.planInfo?.nombre).filter(Boolean);
    return ['todos', ...Array.from(new Set(planes))];
  }, [tareas]);

  useEffect(() => {
    fetchTareasEnProceso();
  }, [fetchTareasEnProceso]);

  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [error, clearError]);

  // Definir las columnas de la tabla
  const columns = [
    {
      id: 'numero_orden',
      header: 'Número Orden',
      accessorFn: (row) => row.ordenInfo?.numero,
      cell: ({ row }) => {
        const numero = row.original.ordenInfo?.numero;
        return numero ? `${numero}` : 'N/A';
      },
      size: 120
    },
    {
      id: 'nombre_producto',
      header: 'Producto',
      accessorKey: 'ordenInfo.nombre_producto',
      cell: ({ row }) => {
        const nombre = row.original.ordenInfo?.nombre_producto;
        return nombre || 'N/A';
      },
      size: 200
    },
    {
      id: 'plan',
      header: 'Plan Asignado',
      accessorKey: 'planInfo.nombre',
      cell: ({ row }) => {
        const nombre = row.original.planInfo?.nombre;
        return nombre || 'N/A';
      },
      size: 180
    },
    {
      id: 'inicio',
      header: 'Fecha de Inicio',
      accessorKey: 'inicio',
      cell: ({ row }) => {
        const fecha = row.original.inicio;
        if (!fecha) return 'N/A';
        
        const date = new Date(fecha);
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
      size: 150
    },
    {
      id: 'progreso',
      header: 'Progreso Centros de Costo',
      accessorKey: 'progreso',
      cell: ({ row }) => {
        const progreso = row.original.progreso;
        if (!progreso) return 'N/A';
        
        const porcentaje = progreso.porcentaje || 0;
        const colorBarra = porcentaje === 100 ? 'bg-success' : 
                           porcentaje > 50 ? 'bg-info' : 
                           porcentaje > 25 ? 'bg-warning' : 'bg-danger';
        
        return (
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center mb-1">
              <div className="progress flex-grow-1 me-2" style={{ height: '10px' }}>
                <div
                  className={`progress-bar ${colorBarra}`}
                  role="progressbar"
                  style={{ width: `${porcentaje}%` }}
                  aria-valuenow={porcentaje}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
              <small className="text-muted fw-bold">
                {porcentaje}%
              </small>
            </div>
            <div className="d-flex justify-content-between small text-muted">
              <span>Completados: {progreso.completados}</span>
              <span>Pendientes: {progreso.pendientes}</span>
              <span>Total: {progreso.total}</span>
            </div>
          </div>
        );
      },
      size: 250
    },
    {
      id: 'estado',
      header: 'Estado',
      accessorKey: 'progreso',
      cell: ({ row }) => {
        const progreso = row.original.progreso;
        if (!progreso) return 'N/A';
        
        if (progreso.pendientes === 0) {
          return <span className="badge bg-success">Completada</span>;
        } else if (progreso.completados === 0) {
          return <span className="badge bg-warning">Pendiente</span>;
        } else {
          return <span className="badge bg-info">En Proceso</span>;
        }
      },
      size: 120
    },
    {
      id: 'tiempo_transcurrido',
      header: 'Tiempo Transcurrido',
      accessorKey: 'inicio',
      cell: ({ row }) => {
        const inicio = row.original.inicio;
        if (!inicio) return 'N/A';
        
        const ahora = new Date();
        const fechaInicio = new Date(inicio);
        const diffMs = ahora - fechaInicio;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (diffDays > 0) {
          return `${diffDays}d ${diffHours}h`;
        } else if (diffHours > 0) {
          return `${diffHours}h`;
        } else {
          const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          return `${diffMinutes}m`;
        }
      },
      size: 140
    },
    {
      id: 'acciones',
      header: 'Acciones',
      cell: ({ row }) => {
        const rowId = row.original.id;
        return (
          <div className="d-flex gap-2">
            <button
              id={`detalles-${rowId}`}
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleVerDetalles(row.original.id)}
              title="Asignar Usuarios"
            >
              <i className="fas fa-pen"></i>
            </button>
            <UncontrolledTooltip placement="top" target={`detalles-${rowId}`}>
              Asignar usuarios
            </UncontrolledTooltip>

            <button
              id={`pdf-${rowId}`}
              className="btn btn-sm btn-success"
              onClick={() => handlePdf(row.original)}
              title="Ver PDF"
            >
              <i className="fas fa-file-pdf"></i>
            </button>
            <UncontrolledTooltip placement="top" target={`pdf-${rowId}`}>
              Ver PDF
            </UncontrolledTooltip>

            {/* Botón de imprimir solo para órdenes externas */}
            {row.original.ordenInfo?.trabajo_interno === false && (
              <>
                <button
                  id={`print-${rowId}`}
                  className="btn btn-sm btn-secondary"
                  onClick={() => handlePrintExterna(row.original)}
                  title="Imprimir PDF sin cliente ni asesor"
                >
                  <i className="fas fa-print"></i>
                </button>
                <UncontrolledTooltip placement="top" target={`print-${rowId}`}>
                  Imprimir PDF sin cliente ni asesor
                </UncontrolledTooltip>
              </>
            )}

            <button
              id={`estadisticas-${rowId}`}
              className="btn btn-info btn-sm"
              onClick={() => handleMostrarEstadisticas(row.original.ordenInfo?.id)}
              title="Ver estadísticas de la orden"
            >
              <i className="fas fa-chart-line"></i>
            </button>
            <UncontrolledTooltip placement="top" target={`estadisticas-${rowId}`}>
              Ver estadísticas
            </UncontrolledTooltip>
          </div>
        );
      },
      size: 150
    }
  ];

  // Función para detectar dispositivos móviles
  const detectMobile = () => {
    try {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
      
      // Detectar iOS específicamente (iPhone, iPad, iPod)
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      
      // Detectar Android específicamente
      const isAndroid = /android/i.test(userAgent);
      
      // Detectar otros dispositivos móviles comunes
      const isMobileUA = /Mobi|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      // Detectar por capacidades touch
      const isTouchDevice = typeof window !== 'undefined' && (
        'ontouchstart' in window || 
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
        (window.DocumentTouch && document instanceof window.DocumentTouch)
      );
      
      // Detectar por tamaño de pantalla
      const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;
      
      // Detectar por orientation API (común en móviles)
      const hasOrientationAPI = typeof window !== 'undefined' && 'orientation' in window;
      
      // Combinar todas las detecciones
      const isMobile = isIOS || isAndroid || isMobileUA || 
                      (isTouchDevice && isSmallScreen) || 
                      (isTouchDevice && hasOrientationAPI);
      
      return isMobile;
    } catch (error) {
      tareasLogger.error('Error al detectar dispositivo móvil', error);
      // En caso de error, asumir escritorio
      return false;
    }
  };

  const handlePdf = async (tarea) => {
    try {
      const isMobile = detectMobile();
      
      if (isMobile) {
        // En móviles, usar el método original que funciona (generatePDF con save)
        tareasLogger.info('Dispositivo móvil detectado - usando descarga directa', { 
          ordenId: tarea.ordenInfo?.id, 
          userAgent: navigator.userAgent 
        });
        
        try {
          // Importar dinámicamente la función de impresión
          const { printOrdenProduccion } = await import('../Ordenes/pdf/pdf_print');
          await printOrdenProduccion(tarea.ordenInfo?.id);
          
          tareasLogger.info('PDF generado exitosamente en móvil', { ordenId: tarea.ordenInfo?.id });
          
        } catch (mobileError) {
          tareasLogger.error('Error al generar PDF en móvil', { error: mobileError, ordenId: tarea.ordenInfo?.id });
        }
        
      } else {
        // En desktop, usar modal PDF
        tareasLogger.info('Dispositivo desktop detectado - usando modal PDF', { 
          ordenId: tarea.ordenInfo?.id 
        });
        
        setPdfModalData({ 
          ordenId: tarea.ordenInfo?.id, 
          filename: `orden_produccion_${tarea.ordenInfo?.numero}.pdf` 
        });
        setIsPdfModalOpen(true);
      }
    } catch (error) {
      tareasLogger.error('Error al generar PDF', { error, ordenId: tarea.ordenInfo?.id });
    }
  };

  const handlePrintExterna = async (tarea) => {
    try {
      const isMobile = detectMobile();
      
      if (isMobile) {
        // En móviles, usar descarga directa
        tareasLogger.info('Dispositivo móvil detectado - usando descarga directa para PDF externo', { 
          ordenId: tarea.ordenInfo?.id, 
          userAgent: navigator.userAgent 
        });
        
        try {
          // Importar dinámicamente la función de impresión externa
          const { printOrdenProduccionExterna } = await import('../Ordenes/pdf/pdf_print');
          await printOrdenProduccionExterna(tarea.ordenInfo?.id);
          
          tareasLogger.info('PDF externo generado exitosamente en móvil', { ordenId: tarea.ordenInfo?.id });
          
        } catch (mobileError) {
          tareasLogger.error('Error al generar PDF externo en móvil', { error: mobileError, ordenId: tarea.ordenInfo?.id });
        }
        
      } else {
        // En desktop, usar modal PDF
        tareasLogger.info('Dispositivo desktop detectado - usando modal PDF externo', { 
          ordenId: tarea.ordenInfo?.id 
        });
        
        setPdfModalData({ 
          ordenId: tarea.ordenInfo?.id, 
          filename: `orden_produccion_externa_${tarea.ordenInfo?.numero}.pdf` 
        });
        setIsPdfModalOpen(true);
      }
    } catch (error) {
      tareasLogger.error('Error al generar PDF externo', { error, ordenId: tarea.ordenInfo?.id });
    }
  };

  const handleVerDetalles = (tareaId) => {
    setTareaSeleccionada(tareaId);
    setIsAsignarUsuarioModalOpen(true);
  };

  const handleAsignacionSuccess = () => {
    // Recargar las tareas después de asignar usuarios
    fetchTareasEnProceso();
  };

  const handleMostrarEstadisticas = (ordenId) => {
    // Buscar la tarea que corresponde a esta orden
    const tarea = tareas.find(t => t.ordenInfo?.id === ordenId);
    if (tarea) {
      setOrdenParaEstadisticas({
        id: ordenId,
        numero: tarea.ordenInfo?.numero,
        nombre_producto: tarea.ordenInfo?.nombre_producto,
        cliente: tarea.ordenInfo?.cliente
      });
      setModalEstadisticas(true);
    }
  };

  const handleCloseEstadisticas = () => {
    setModalEstadisticas(false);
    setOrdenParaEstadisticas(null);
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      estado: 'todos',
      plan: 'todos',
      orden: ''
    });
    // Resetear paginación al limpiar filtros
    setPaginacion({
      pagina: 1,
      porPagina: 25
    });
  };

  const cambiarPagina = (nuevaPagina) => {
    setPaginacion(prev => ({
      ...prev,
      pagina: nuevaPagina
    }));
  };

  const cambiarPorPagina = (nuevoPorPagina) => {
    setPaginacion({
      pagina: 1, // Resetear a primera página
      porPagina: nuevoPorPagina
    });
  };

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumb */}
        <Row>
          <Col xs="12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0">Tareas en Proceso</h4>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/">Inicio</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/produccion">Producción</Link>
                  </li>
                  <li className="breadcrumb-item active">Tareas</li>
                </ol>
              </div>
            </div>
          </Col>
        </Row>

        {/* Main Content */}
        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    Lista de Tareas en Proceso
                  </h5>
                  <button
                    className="btn btn-primary"
                    onClick={fetchTareasEnProceso}
                    disabled={loading}
                  >
                    <i className="fas fa-sync-alt me-1"></i>
                    {loading ? 'Actualizando...' : 'Actualizar'}
                  </button>
                </div>

                {/* Filtros */}
                <div className="mb-3">
                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="filtro-estado">Estado</Label>
                        <Input
                          id="filtro-estado"
                          type="select"
                          value={filtros.estado}
                          onChange={(e) => handleFiltroChange('estado', e.target.value)}
                        >
                          <option value="todos">Todos los Estados</option>
                          <option value="pendiente">Pendiente</option>
                          <option value="en_proceso">En Proceso</option>
                          <option value="completada">Completada</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="filtro-plan">Plan</Label>
                        <Input
                          id="filtro-plan"
                          type="select"
                          value={filtros.plan}
                          onChange={(e) => handleFiltroChange('plan', e.target.value)}
                        >
                          {planesUnicos.map(plan => (
                            <option key={plan} value={plan}>
                              {plan === 'todos' ? 'Todos los Planes' : plan}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label for="filtro-orden">Número de Orden</Label>
                        <Input
                          id="filtro-orden"
                          type="text"
                          placeholder="Buscar por número..."
                          value={filtros.orden}
                          onChange={(e) => handleFiltroChange('orden', e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                      <Button
                        color="secondary"
                        outline
                        onClick={limpiarFiltros}
                        className="w-100"
                      >
                        <i className="fas fa-times me-1"></i>
                        Limpiar Filtros
                      </Button>
                    </Col>
                  </Row>
                </div>

                {/* Resumen de resultados */}
                <div className="mb-3">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <small className="text-muted">
                        Mostrando {infoPaginacion.inicio}-{infoPaginacion.fin} de {infoPaginacion.total} tareas
                        {filtros.estado !== 'todos' && ` (Estado: ${filtros.estado})`}
                        {filtros.plan !== 'todos' && ` (Plan: ${filtros.plan})`}
                        {filtros.orden && ` (Orden: ${filtros.orden})`}
                      </small>
                    </Col>
                    <Col md={6} className="text-end">
                      <FormGroup className="d-inline-block me-2" style={{ width: '120px' }}>
                        <Input
                          type="select"
                          value={paginacion.porPagina}
                          onChange={(e) => cambiarPorPagina(Number(e.target.value))}
                          size="sm"
                        >
                          <option value={10}>10 por página</option>
                          <option value={25}>25 por página</option>
                          <option value={50}>50 por página</option>
                          <option value={100}>100 por página</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                </div>

                <DataTableContainer
                  data={tareasPaginadas}
                  columns={columns}
                  loading={loading}
                  error={error}
                  emptyMessage="No hay tareas que coincidan con los filtros aplicados"
                  isGlobalFilter={false}
                />

                {/* Controles de paginación */}
                {infoPaginacion.totalPaginas > 1 && (
                  <div className="mt-3 d-flex justify-content-center">
                    <nav aria-label="Navegación de páginas">
                      <ul className="pagination pagination-sm">
                        {/* Botón Primera Página */}
                        <li className={`page-item ${infoPaginacion.paginaActual === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => cambiarPagina(1)}
                            disabled={infoPaginacion.paginaActual === 1}
                          >
                            <i className="fas fa-angle-double-left"></i>
                          </button>
                        </li>
                        
                        {/* Botón Página Anterior */}
                        <li className={`page-item ${infoPaginacion.paginaActual === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => cambiarPagina(infoPaginacion.paginaActual - 1)}
                            disabled={infoPaginacion.paginaActual === 1}
                          >
                            <i className="fas fa-angle-left"></i>
                          </button>
                        </li>

                        {/* Números de página */}
                        {Array.from({ length: Math.min(5, infoPaginacion.totalPaginas) }, (_, i) => {
                          let numeroPagina;
                          if (infoPaginacion.totalPaginas <= 5) {
                            numeroPagina = i + 1;
                          } else if (infoPaginacion.paginaActual <= 3) {
                            numeroPagina = i + 1;
                          } else if (infoPaginacion.paginaActual >= infoPaginacion.totalPaginas - 2) {
                            numeroPagina = infoPaginacion.totalPaginas - 4 + i;
                          } else {
                            numeroPagina = infoPaginacion.paginaActual - 2 + i;
                          }
                          
                          return (
                            <li
                              key={numeroPagina}
                              className={`page-item ${infoPaginacion.paginaActual === numeroPagina ? 'active' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => cambiarPagina(numeroPagina)}
                              >
                                {numeroPagina}
                              </button>
                            </li>
                          );
                        })}

                        {/* Botón Página Siguiente */}
                        <li className={`page-item ${infoPaginacion.paginaActual === infoPaginacion.totalPaginas ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => cambiarPagina(infoPaginacion.paginaActual + 1)}
                            disabled={infoPaginacion.paginaActual === infoPaginacion.totalPaginas}
                          >
                            <i className="fas fa-angle-right"></i>
                          </button>
                        </li>

                        {/* Botón Última Página */}
                        <li className={`page-item ${infoPaginacion.paginaActual === infoPaginacion.totalPaginas ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => cambiarPagina(infoPaginacion.totalPaginas)}
                            disabled={infoPaginacion.paginaActual === infoPaginacion.totalPaginas}
                          >
                            <i className="fas fa-angle-double-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal PDF Viewer */}
      <PDFViewerModal
        isOpen={isPdfModalOpen}
        toggle={() => setIsPdfModalOpen(false)}
        ordenId={pdfModalData.ordenId}
        filename={pdfModalData.filename}
        titulo="PDF de Orden de Producción"
      />

      {/* Modal Asignar Usuarios */}
      <AsignarUsuarioModal
        isOpen={isAsignarUsuarioModalOpen}
        toggle={() => setIsAsignarUsuarioModalOpen(false)}
        tareaId={tareaSeleccionada}
        onSuccess={handleAsignacionSuccess}
      />

      {/* Modal de Estadísticas */}
      {modalEstadisticas && ordenParaEstadisticas && (
        <EstadisticasOrdenModal
          isOpen={modalEstadisticas}
          onClose={handleCloseEstadisticas}
          ordenId={ordenParaEstadisticas.id}
          ordenData={ordenParaEstadisticas}
        />
      )}
    </div>
  );
};

export default TareasEnProceso;
